import Head from 'next/head';
import type { GetStaticProps, InferGetStaticPropsType } from 'next';
import { NextPageWithLayout } from '@/pages/_app';
import React from 'react';
import Layout from '@/index-layouts';
import { AppSettings } from '@/shared/app-common';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import dynamic from 'next/dynamic';
const HomeContent = dynamic(() => import('@/components/onlyfans-competitors'), {
  ssr: true,
});
import { matchPlatform } from '@/lib/hooks/utils/match-platform';
import { getTdkApi } from '@/lib/service/tdk';
import { page2JsonLd } from '@/lib/common/jsonld';
import { hyphenatedToTitleCase } from '@/shared/path-helper';
import { getPageContentContentApi } from '@/lib/service/home';
import { DynamicISRDebugPanel as ISRDebugPanel } from '@/components/ISRDebugPanel/dynamic';
import { ISR_REVALIDATE_TIME } from '@/lib/config/isr';

// ⭐ ISR: 转换为静态生成 + 7 天重新验证
export const getStaticProps: GetStaticProps = async (context) => {
  const { locale = 'en' } = context;
  const resolvedUrl = '/compare';

  let currentURL = `${process.env.NEXT_APP_WEB_HOST}${resolvedUrl}`;
  if (locale != 'en') {
    currentURL = `${process.env.NEXT_APP_WEB_HOST}/${locale}${resolvedUrl}`;
  }
  const [localeRes, tdkData, res] = await Promise.all([
    serverSideTranslations(locale, ['common']),
    getTdkApi(resolvedUrl, locale),
    getPageContentContentApi(
      `/compare`,
      'en',
      '', // ISR 不支持 preview 参数
      '',
    ),
  ]);
  const tempData = localeRes['_nextI18Next']['initialI18nStore'][locale]['common'] ?? {};

  // const articleList = await getArticleRecommendList(null, locale);
  const platList = await matchPlatform();
  const siteUrl = locale !== 'en' ? `${process.env.NEXT_APP_WEB_HOST}/${locale}` : `${process.env.NEXT_APP_WEB_HOST}`;
  const pageTitle = tdkData?.data?.title ?? tempData?.head_Meta_Title_OnlyfansCompetitors ?? null;
  const pageDesc = tdkData?.data?.description ?? tempData?.head_Meta_Desc_OnlyfansCompetitors ?? null;
  const pageKeywords = tdkData?.data?.keywords ?? tempData?.head_Meta_Keys ?? null;

  const jsonLd = page2JsonLd(
    {
      title: hyphenatedToTitleCase(resolvedUrl),
      desc: pageDesc,
      url: currentURL,
    },
    {
      url: siteUrl,
    },
  );
  return {
    props: {
      ...localeRes,
      herf: currentURL,
      locale,
      platList,
      title: pageTitle,
      description: pageDesc,
      keywords: pageKeywords,
      jsonLd: jsonLd,
      data: res.data,
      // ⭐ ISR 信息
      isrGeneratedAt: new Date().toISOString(),
      isrRevalidateIn: ISR_REVALIDATE_TIME,
    },
    revalidate: ISR_REVALIDATE_TIME,
  };
};

const Home: NextPageWithLayout<InferGetStaticPropsType<typeof getStaticProps>> = (props: any) => {
  return (
    <>
      <ISRDebugPanel
        generatedAt={props.isrGeneratedAt}
        revalidateIn={props.isrRevalidateIn}
        pageName="Compare"
        additionalInfo={{
          语言: props.locale,
        }}
      />
      <HomeContent data={props} platList={props.platList} />
    </>
  );
};
Home.getLayout = function getLayout(page: React.ReactElement) {
  const title = page?.props?.title;
  const desc = page?.props?.description;
  const keywords = page?.props?.keywords;
  const jsonLd = page?.props?.jsonLd;
  return (
    <Layout
      head={
        <Head>
          <title>{title}</title>
          <meta name="title" content={title} />
          <meta name="description" content={desc} />
          <meta name="keywords" content={keywords} />

          <meta property="og:title" content={title} />
          <meta property="og:description" content={desc} />
          <meta
            property="og:image"
            content={'https://res-front.pumpsoul.com/prod-erasa/image/dmca-protect/dmca-5.png'}
          />
          <meta
            property="og:image:secure_url"
            content={'https://res-front.pumpsoul.com/prod-erasa/image/dmca-protect/dmca-5.png'}
          />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={page.props?.herf} />

          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={desc} />
          <meta
            name="twitter:image"
            content={'https://res-front.pumpsoul.com/prod-erasa/image/dmca-protect/dmca-5.png'}
          />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:site" content={page.props?.herf} />

          <meta
            name="viewport"
            content="width=device-width, initial-scale=1, minimum-scale=1, maximum-scale=1, shrink-to-fit=no, user-scalable=no, viewport-fit=cover"
          />
          <link rel="alternate" hrefLang="en" href={`${process.env.NEXT_APP_WEB_HOST}/en/compare`} />
          <link rel="alternate" hrefLang="zh" href={`${process.env.NEXT_APP_WEB_HOST}/zh/compare`} />
          <link rel="alternate" hrefLang="tw" href={`${process.env.NEXT_APP_WEB_HOST}/tw/compare`} />
          <link rel="alternate" hrefLang="ja" href={`${process.env.NEXT_APP_WEB_HOST}/ja/compare`} />
          <link rel="alternate" hrefLang="ko" href={`${process.env.NEXT_APP_WEB_HOST}/ko/compare`} />
          <link rel="alternate" hrefLang="pt" href={`${process.env.NEXT_APP_WEB_HOST}/pt/compare`} />
          <link rel="alternate" hrefLang="de" href={`${process.env.NEXT_APP_WEB_HOST}/de/compare`} />
          <link rel="alternate" hrefLang="es" href={`${process.env.NEXT_APP_WEB_HOST}/es/compare`} />
          <link rel="alternate" hrefLang="it" href={`${process.env.NEXT_APP_WEB_HOST}/it/compare`} />

          <link rel="alternate" hrefLang="x-default" href={`${process.env.NEXT_APP_WEB_HOST}/compare`} />
          <link
            rel="preload"
            href="https://res-front.pumpsoul.com/prod-erasa/image/competion.webp"
            as="image"
            fetchPriority="high"
          />
          <link rel="canonical" href={page.props?.herf} />
          <link rel="icon" href={AppSettings.icoPath} />
          {jsonLd && (
            <script async type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
          )}
        </Head>
      }
    >
      {page}
    </Layout>
  );
};

export default Home;
